/**
 * E002-T018: AutomationConfigPanel
 *
 * Sidebar panel of an automation block: the start page (setup steps, trigger,
 * Run / Activate) and the settings panel with every setting. Both read one
 * readiness model (automation-readiness.ts). Connects directly to the
 * BlockModel for persistence.
 *
 * E002-T040: E2E Integration
 * - Reads rows from source Database
 * - Applies filters and mappings
 * - Provides execution context to ExecutionPanel
 */

import { Loading, notify, Switch } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import {
  type AutomationBlockModel,
  cleanupBindingUpdates,
} from '@blocksuite/affine/blocks/streamlain';
import {
  ArrowLeftSmallIcon,
  PlayIcon,
  SettingsIcon,
  TableIcon,
} from '@blocksuite/icons/rc';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useEnableCloud } from '../../../components/hooks/affine/use-enable-cloud';
import { PublishHubEntry } from '../../../desktop/dialogs/marketplace/publish-hub-entry';
import { AuthService } from '../../cloud';
import { GlobalDialogService } from '../../dialogs';
import { DocsService } from '../../doc';
import { EditorService } from '../../editor';
import { WorkspacePermissionService } from '../../permissions';
import { WorkbenchService } from '../../workbench/services/workbench';
import { WorkspaceService } from '../../workspace';
import {
  useAutomationUIState,
  useBlockModelConfig,
  useChosenSourceRows,
} from '../hooks';
import { useAiKeyCheck } from '../hooks/use-ai-key-check';
import { useAutomationBlockModel } from '../hooks/use-automation-block-model';
import { extractConfig } from '../hooks/use-block-model-config';
import {
  FEATURE_KEY_WORKFLOW,
  useFeatureGate,
} from '../hooks/use-feature-gate';
import { useInboundWebhooks } from '../hooks/use-inbound-webhooks';
import { useWorkflowPermissions } from '../hooks/use-workflow-permissions';
import type { AutomationConfig } from '../services/automation';
import {
  AutomationService,
  DEFAULT_STATUS_TRACKING_CONFIG,
} from '../services/automation';
import {
  AutomationConfigApiService,
  type ConfigReadResponse,
  isAutomationTakeoverRequired,
} from '../services/automation-config-api';
import { AutomationDiscoveryService } from '../services/automation-discovery';
import { setAutomationReadinessFacts } from '../services/automation-runtime-bridge';
import {
  automationSafetyEnableFailureKey,
  isCurrentAutomationSafety,
  presentAutomationSafety,
  presentLoopPathSegment,
} from '../services/automation-safety-presentation';
import { AutomationSocketService } from '../services/automation-socket';
import {
  CrossDocAutomationService,
  PermissionDeniedError,
} from '../services/cross-doc-automation';
import type { ColumnInfo } from '../services/database-discovery';
import { DatabaseDiscoveryService } from '../services/database-discovery';
import {
  applyFilters,
  processRowsForExecution,
} from '../services/database-rows';
import { normalizeBatchConfig } from '../services/execution';
import { type ExecutionLog, LoggingService } from '../services/logging';
// Note: ExecutionService is accessed via useAutomationUIState hook (E018-S007-T001)
import { WorkflowService } from '../services/workflow';
import {
  type AutomationSafetyDiagnosticPage,
  WorkflowClientService,
} from '../services/workflow-client';
import { WorkflowTestContextService } from '../services/workflow-test-context';
import {
  buildAutomationTemplatePublishSource,
  canOpenAutomationTemplatePublish,
} from '../template-publish-source';
import { useRegisterAutomationSessions } from '../tutorial/sessions/automation';
import { usePermissionOverviewTrigger } from '../tutorial/sessions/automation/use-permission-overview-trigger';
import { isActivatableTrigger } from '../types';
import {
  resolveAutomationDetailTitle,
  writeAutomationTitle,
} from '../utils/automation-title';
import { AutomationGroupSelector } from './automation-group-selector';
import {
  hasConfiguredAutomationInput,
  trackedStatusColumnId,
} from './automation-input-requirements';
import * as styles from './automation-panel.css';
import {
  computeAutomationReadiness,
  countReadyParts,
  READINESS_PART_COUNT,
  type ReadinessFacts,
  type ReadinessIssue,
  type ReadinessTileId,
} from './automation-readiness';
import { AutomationSettingsPanel } from './automation-settings-panel';
import * as settingsStyles from './automation-settings-panel.css';
import { AutomationStartPage } from './automation-start-page';
import * as startStyles from './automation-start-page.css';
import { AutomationTakeoverDialog } from './automation-takeover-dialog';
import * as takeoverStyles from './automation-takeover-dialog.css';
import { CleanupPanel } from './cleanup-panel';
import { ExecutionPanel } from './components/execution-panel';
import { HistoryPanel } from './components/history-panel';
import { formatHistoryExecutionStatusLabel } from './components/history-status-labels';
import {
  HubShareSection,
  type HubShareState,
  hubShareSummaryKey,
} from './components/hub-share-section';
import { openWorkflowBuilder } from './components/open-workflow';
import { flushPreFlightDocs } from './components/preflight-comparison';
import {
  preFlightDatabaseDirectories,
  preFlightLocalDocs,
} from './components/preflight-local-docs';
import { RowHistoryPanel } from './components/row-history-panel';
import { SettingSection, SettingUnit } from './components/setting-section';
import { isMailFolderTable } from './components/source-table-rules';
import { computeDatabaseReferenceMoveUpdates } from './database-reference-reconciliation';
import { InputTab } from './input-tab';
import { OutputTab } from './output-tab';
import { ProcessTab } from './process-tab';
import { createSelectColumnOptions } from './select-column-options';
import {
  PANE_PRIMARY_TILE,
  paneOfTile,
  type SettingsPane,
  tilesOfPane,
} from './settings-pane';
import {
  addRelationColumn,
  addSourceTableColumn,
} from './source-relation-column';
import { TRIGGER_ICONS } from './trigger-icons';
import { TriggerSettings } from './trigger-settings';
import { useAutomationSettingsHost } from './use-automation-settings-host';
import { useWorkflowStepCache } from './use-workflow-step-cache';
import {
  createWebhookInboxTable,
  WebhookInboxMappingError,
  type WebhookInboxTable,
} from './webhook-store-table';
import { mappableInputs } from './workflow-row-list';
import type { WorkflowSwitchDeps } from './workflow-switch-handler';
import { WorkspaceAutomationsPanel } from './workspace-automations-panel';

/** The settings framed together in the panes (see `SettingUnit`). */
const WORKFLOW_UNIT_TILES = ['workflow', 'mapping', 'test'] as const;
const FAILURE_UNIT_TILES = ['failure'] as const;
const TRIGGER_UNIT_TILES = ['trigger'] as const;
const HISTORY_UNIT_TILES = ['history'] as const;

/**
 * AutomationConfigPanel - Main sidebar component
 *
 * Registered via ExtensionSidebarService in streamlain module.
 */
export const AutomationConfigPanel: FC = () => {
  const automationService = useService(AutomationService);
  const automationDiscoveryService = useService(AutomationDiscoveryService);
  const databaseDiscoveryService = useService(DatabaseDiscoveryService);
  const workflowService = useService(WorkflowService);
  const workflowClient = useService(WorkflowClientService);
  const workflowTestContext = useService(WorkflowTestContextService);
  const loggingService = useService(LoggingService);
  // Note: ExecutionService is accessed via useAutomationUIState hook
  const editorService = useServiceOptional(EditorService);
  const workspaceService = useService(WorkspaceService);
  const workbenchService = useService(WorkbenchService);
  const docsService = useService(DocsService);
  const crossDocAutomationService = useService(CrossDocAutomationService);
  // Initialize socket connection for real-time log updates (side-effect only)
  useService(AutomationSocketService);

  // sl-u319.1.3: Feature gate for marketplace publishing
  const { isFeatureAllowed, isLoading: isFeatureGateLoading } =
    useFeatureGate();
  const publishAllowed = isFeatureAllowed('marketplace-publish');
  // Webhook triggers are a workflow-plan feature.
  const hasWorkflowFeature = isFeatureAllowed(FEATURE_KEY_WORKFLOW);
  const permissionService = useServiceOptional(WorkspacePermissionService);
  const canPublishMarketplace =
    useLiveData(permissionService?.permission.isOwnerOrAdmin$ ?? null) === true;

  // sl-728o.3.1: Register automation tutorial sessions
  useRegisterAutomationSessions();

  // sl-trigger-explicit: Config API for schedule operations
  const configApiService = useService(AutomationConfigApiService);

  // sl-trigger-explicit: Schedule saving state
  const [isScheduleSaving, setIsScheduleSaving] = useState(false);
  const [isStoringWebhook, setIsStoringWebhook] = useState(false);
  const pendingWebhookInbox = useRef<{
    blockId: string;
    result: WebhookInboxTable | null;
  } | null>(null);
  const [safetyDiagnostic, setSafetyDiagnostic] =
    useState<AutomationSafetyDiagnosticPage | null>(null);
  const [statusColumnShared, setStatusColumnShared] = useState(false);
  // The config as the server reads it: readiness facts and who it runs as.
  const [configRead, setConfigRead] = useState<ConfigReadResponse | null>(null);
  const [takeoverOpen, setTakeoverOpen] = useState(false);

  const activeBlockId = useLiveData(automationService.activeBlockId$);
  const rowHistoryTarget = useLiveData(automationService.rowHistoryTarget$);
  const settingsHost = useAutomationSettingsHost();
  const databases = useLiveData(databaseDiscoveryService.databases$);

  // E024-S001-T001: Check workspace type - Automations only available in Cloud
  const workspace = workspaceService.workspace;
  const isLocalStorage = workspace.flavour === 'local';
  const confirmEnableCloud = useEnableCloud();

  // E024: Auth state for "Sign in and Enable" vs "Enable Cloud" button
  const authService = useService(AuthService);
  const loginStatus = useLiveData(authService.session.status$);
  const isLoggedIn = loginStatus === 'authenticated';
  const globalDialogService = useService(GlobalDialogService);
  const t = useI18n();

  // sl-3nk: Get current user for ownership check (AI settings only editable by owner)
  const currentAccount = useLiveData(authService.session.account$);
  const currentUserId = currentAccount?.id ?? undefined;

  const handleEnableCloud = useCallback(() => {
    if (isLoggedIn) {
      confirmEnableCloud(workspace);
    } else {
      globalDialogService.open('sign-in', {});
    }
  }, [isLoggedIn, confirmEnableCloud, workspace, globalDialogService]);

  // E018-S014-T004: Watch automation info for cross-doc lookup
  const automationInfoLive$ = activeBlockId
    ? automationDiscoveryService.watchAutomation(activeBlockId)
    : null;
  const automationInfo = useLiveData(automationInfoLive$);
  const workflows = useLiveData(workflowService.workflows$);
  // sl-tq87: Schema used for validation bypass (discovery not mandatory when schema present)
  const workflowSchema = useLiveData(workflowService.selectedWorkflowSchema$);

  /** The settings panel's open tile; `null` while the panel is closed. */
  const [settingsTile, setSettingsTile] = useState<ReadinessTileId | null>(
    null
  );
  // The setting an open item led to: pointed out until its signal ends or
  // another setting is shown.
  const [attentionTile, setAttentionTile] = useState<ReadinessTileId | null>(
    null
  );
  const clearAttention = useCallback(() => setAttentionTile(null), []);
  /** The input filter decides the rows a row trigger runs; show it. */
  const openFilterSetting = useCallback(() => {
    setSettingsTile('filter');
    setAttentionTile('filter');
  }, []);
  useEffect(() => {
    if (attentionTile !== null && attentionTile !== settingsTile)
      setAttentionTile(null);
  }, [attentionTile, settingsTile]);
  const [publishWizardOpen, setPublishWizardOpen] = useState(false);

  // The settings pane belongs to one automation.
  useEffect(() => {
    setSettingsTile(null);
  }, [activeBlockId]);

  // A jump from the canvas block opens its setting (after the reset above).
  const pendingSettingsTarget = useLiveData(
    automationService.pendingSettingsTarget$
  );
  useEffect(() => {
    if (!activeBlockId || pendingSettingsTarget?.blockId !== activeBlockId)
      return;
    const target =
      automationService.consumePendingSettingsTarget(activeBlockId);
    if (!target) return;
    setSettingsTile(target.tile);
    setAttentionTile(target.attention ? target.tile : null);
  }, [activeBlockId, automationService, pendingSettingsTarget]);

  // Get BlockModel directly from BlockSuite
  // E018-S014-T004: Pass automationInfo.docId for cross-doc block lookup
  const blockModel = useAutomationBlockModel(
    activeBlockId,
    automationInfo?.docId,
    docsService
  );

  // E018-S007-T001: SINGLE SOURCE OF TRUTH
  // Config is read reactively from BlockModel via subscription to propsUpdated
  // NO local useState<config> - eliminates stale state possibility
  const config = useBlockModelConfig(blockModel);

  useEffect(() => {
    const workflowId = config?.workflowId;
    const docId = automationInfo?.docId;
    if (!activeBlockId || !workflowId || !docId || !workspace.id) {
      setSafetyDiagnostic(null);
      return;
    }
    let current = true;
    void configApiService
      .getConfig(activeBlockId, workspace.id, docId)
      .then(async saved =>
        saved
          ? {
              saved,
              safety: await workflowClient
                .getAutomationSafety(workflowId, saved.id)
                .catch(error => {
                  if (current)
                    notify.warning({
                      title:
                        t[
                          'com.streamlain.streamlain.workflow.failed-to-load'
                        ](),
                      message:
                        error instanceof Error ? error.message : String(error),
                    });
                  throw error;
                }),
            }
          : null
      )
      .then(result => {
        if (!current) return;
        if (
          result?.safety &&
          isCurrentAutomationSafety(result.safety, workflowId, result.saved.id)
        ) {
          setSafetyDiagnostic(result.safety);
        } else {
          setSafetyDiagnostic(null);
        }
      })
      .catch(() => {
        if (current) setSafetyDiagnostic(null);
      });
    return () => {
      current = false;
    };
  }, [
    activeBlockId,
    automationInfo?.docId,
    config?.workflowId,
    configApiService,
    t,
    workflowClient,
    workspace.id,
  ]);

  // Server facts: another automatic automation on the source table writes
  // this status column, and who a switched-on automation runs as. Read again
  // when what decides them changes.
  const statusColumnKey = [
    config?.sourceTableId ?? '',
    config?.triggerConfig?.type ?? '',
    trackedStatusColumnId(config?.statusTracking) ?? '',
    config?.triggerConfig?.enabled ? 'on' : 'off',
  ].join(':');
  useEffect(() => {
    const docId = automationInfo?.docId;
    if (!activeBlockId || !docId || !workspace.id) {
      setStatusColumnShared(false);
      return;
    }
    let current = true;
    void configApiService
      .getConfig(activeBlockId, workspace.id, docId)
      .then(saved => {
        if (!current) return;
        setConfigRead(saved);
        setStatusColumnShared(saved?.statusColumnShared ?? false);
      });
    return () => {
      current = false;
    };
  }, [
    activeBlockId,
    automationInfo?.docId,
    configApiService,
    statusColumnKey,
    workspace.id,
  ]);

  // sl-kqzl.5: Fire the permission-overview coachmark once after first setup.
  usePermissionOverviewTrigger(config?.workflowId ?? null);

  // sl-za0v.10: RBAC — check workflow permissions for UI gating
  const { permissions: wfPermissions } = useWorkflowPermissions(
    config?.workflowId ?? null
  );

  const liveWorkflowName = config?.workflowId
    ? workflows.find(w => w.id === config.workflowId)?.name
    : undefined;
  const detailAutomationTitle = resolveAutomationDetailTitle(
    automationInfo?.name,
    {
      title: config?.title,
      titleMode: config?.titleMode,
      workflowName: liveWorkflowName ?? config?.workflowName,
    },
    t['com.streamlain.streamlain.common.new-automation']()
  );
  const [titleDraft, setTitleDraft] = useState(detailAutomationTitle);
  const [isTitleEditing, setIsTitleEditing] = useState(false);

  useEffect(() => {
    if (!isTitleEditing) setTitleDraft(detailAutomationTitle);
  }, [activeBlockId, detailAutomationTitle, isTitleEditing]);

  const commitTitleDraft = useCallback(() => {
    const didWrite = writeAutomationTitle(
      blockModel as { props: Record<string, unknown> } | null,
      titleDraft,
      'user'
    );
    setIsTitleEditing(false);
    if (!didWrite) setTitleDraft(detailAutomationTitle);
  }, [blockModel, detailAutomationTitle, titleDraft]);

  const detailTitleEditor = (
    <input
      className={styles.titleInput}
      disabled={!wfPermissions.canEdit}
      value={titleDraft}
      onChange={event => setTitleDraft(event.target.value)}
      onFocus={() => setIsTitleEditing(true)}
      onBlur={commitTitleDraft}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          setTitleDraft(detailAutomationTitle);
          setIsTitleEditing(false);
          event.currentTarget.blur();
        }
      }}
      placeholder={t['com.streamlain.streamlain.common.new-automation']()}
      aria-label={t['com.streamlain.streamlain.common.new-automation']()}
    />
  );

  // Clear stale schema immediately on automation switch to prevent cross-contamination.
  // Without this, selectedWorkflowSchema$ retains the previous automation's schema
  // until the new one loads (or forever if the new automation has no workflow).
  useEffect(() => {
    workflowService.selectedWorkflowSchema$.next(null);
  }, [activeBlockId, workflowService]);

  // Load schema on mount when workflowId exists (Page Reload Fix)
  // This ensures schema is available immediately for Input Tab
  // Primary loading on workflow change happens in process-tab.tsx
  useEffect(() => {
    if (config?.workflowId) {
      workflowService.loadWorkflowSchema(config.workflowId).catch(error => {
        notify.warning({
          title: t['com.streamlain.streamlain.input.schema-load-failed'](),
          message: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, [config?.workflowId, workflowService, t]);

  // Get editor doc for reading database rows
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const currentDoc = editorContainer?.doc;
  const currentDocId = currentDoc?.id;

  // E018-S014-T004: Cross-Doc aware row processing
  // For same-doc: synchronous processing from currentDoc
  // For cross-doc: async loading via CrossDocAutomationService with proper cleanup
  const isCrossDoc = config?.sourceDocId && config.sourceDocId !== currentDocId;

  // A manual run over a table runs the rows the user chose in it.
  const manualRunOverTable =
    (config?.triggerConfig?.type ?? 'manual') === 'manual' &&
    !!config?.sourceTableId;
  const chosenSourceRows = useChosenSourceRows({
    enabled: manualRunOverTable,
    sourceTableId: config?.sourceTableId,
    sourceDocId: config?.sourceDocId,
    currentDoc,
  });

  // State for async cross-doc rows
  const [crossDocRows, setCrossDocRows] = useState<ReturnType<
    typeof processRowsForExecution
  > | null>(null);
  const lastCrossDocErrorKeyRef = useRef<string | null>(null);

  // Effect for cross-doc row loading with proper cleanup
  useEffect(() => {
    if (!isCrossDoc || !config?.sourceDocId || !config?.sourceTableId) {
      setCrossDocRows(null);
      lastCrossDocErrorKeyRef.current = null;
      return;
    }

    let cancelled = false;
    const sourceDocId = config.sourceDocId;
    const sourceTableId = config.sourceTableId;
    const loadKey = `${sourceDocId}:${sourceTableId}`;

    const loadCrossDocRows = async () => {
      try {
        const result = await crossDocAutomationService.getSourceRows(
          sourceDocId,
          sourceTableId
        );

        if (cancelled) return;

        if (result.success && result.rows) {
          lastCrossDocErrorKeyRef.current = null;
          // Transform CrossDocAutomationService result to ProcessedRows format
          const databaseTitle =
            config.sourceTableName ||
            t['com.streamlain.streamlain.input.cross-doc-table']();

          // Create DatabaseRow format for allRows/filteredRows
          const databaseRows = result.rows.map(row => ({
            id: row.rowId,
            rowId: row.rowId,
            cells: row.cells,
          }));

          // sl-czam: Apply filters to cross-doc rows (same as same-doc path)
          const filtered = applyFilters(databaseRows, config.filterGroup);

          // Create a compatible result structure matching ProcessedRows interface
          const processedResult = {
            sourceTable: databaseTitle,
            sourceTableId,
            allRows: databaseRows,
            filteredRows: filtered,
            filteredOutCount: databaseRows.length - filtered.length,
            payloads: result.rows.map(row => ({
              rowId: row.rowId,
              ...row.cells,
            })),
            columns: result.columns || [],
          };

          setCrossDocRows(
            processedResult as unknown as ReturnType<
              typeof processRowsForExecution
            >
          );
        } else {
          const message =
            result.error?.message ||
            t['com.streamlain.automation.sidebar.cross-doc-load-failed']();
          const errorKey = `${loadKey}:${result.error?.name ?? 'Error'}:${message}`;
          const shouldReport = lastCrossDocErrorKeyRef.current !== errorKey;
          lastCrossDocErrorKeyRef.current = errorKey;

          if (shouldReport) {
            console.warn(
              '[AutomationPanel] Failed to load cross-doc rows:',
              result.error
            );
          }
          setCrossDocRows(null);

          if (shouldReport) {
            // Show user-facing notification for permission errors
            if (result.error instanceof PermissionDeniedError) {
              notify.error({
                title:
                  t[
                    'com.streamlain.automation.sidebar.cross-doc-denied.title'
                  ](),
                message:
                  t[
                    'com.streamlain.automation.sidebar.cross-doc-denied.message'
                  ](),
              });
            } else if (result.error) {
              notify.error({
                title:
                  t[
                    'com.streamlain.automation.sidebar.cross-doc-unavailable'
                  ](),
                message,
              });
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          const message = e instanceof Error ? e.message : String(e);
          const errorKey = `${loadKey}:unexpected:${message}`;
          const shouldReport = lastCrossDocErrorKeyRef.current !== errorKey;
          lastCrossDocErrorKeyRef.current = errorKey;
          if (shouldReport) {
            console.warn('[AutomationPanel] Cross-doc row loading error:', e);
          }
          setCrossDocRows(null);
        }
      }
    };

    loadCrossDocRows().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [
    isCrossDoc,
    config?.sourceDocId,
    config?.sourceTableId,
    config?.sourceTableName,
    config?.filterGroup,
    crossDocAutomationService,
    t,
  ]);

  // Same-doc row processing (synchronous)
  const sameDocRows = useMemo(() => {
    if (isCrossDoc || !currentDoc || !config?.sourceTableId) return null;

    const sourceDb = databases.find(
      (db: { blockId: string }) => db.blockId === config.sourceTableId
    );
    if (!sourceDb) return null;

    // Note: currentDoc is a Store but processRowsForExecution works with it
    // The row processor accepts the Store shape used by the active editor doc.
    return processRowsForExecution(
      currentDoc as unknown as Parameters<typeof processRowsForExecution>[0],
      config.sourceTableId,
      sourceDb.databaseTitle,
      config.filterGroup,
      config.selectedColumns,
      {
        // E018-WF1: Pass inputMappings to filter preview to only mapped columns
        inputMappings: config.inputMappings,
        // E018-T006: Pass statusTracking for auto-filter in row count
        statusTracking: config.statusTracking,
      }
    );
  }, [
    isCrossDoc,
    currentDoc,
    config?.sourceTableId,
    config?.filterGroup,
    config?.selectedColumns,
    config?.inputMappings,
    config?.statusTracking,
    databases,
  ]);

  // Combined: use cross-doc rows if cross-doc, otherwise same-doc rows
  const processedRows = isCrossDoc ? crossDocRows : sameDocRows;

  // Get selected workflow info
  const selectedWorkflow = useMemo(() => {
    if (!config?.workflowId) {
      return null;
    }
    const found =
      workflows.find((w: { id: string }) => w.id === config.workflowId) ?? null;
    return found;
  }, [config?.workflowId, workflows]);

  // E018-S007-T001: Single Source of Truth for UI State
  const rowCount = processedRows?.payloads?.length ?? 0;
  const uiState = useAutomationUIState(activeBlockId, rowCount);
  const canPublishAutomationTemplate = canOpenAutomationTemplatePublish({
    publishAllowed,
    canPublish: canPublishMarketplace,
    automationBlockId: activeBlockId,
    executionStatus: uiState.badgeStatus,
  });

  // Action items for execution panel configure hint
  // Must be before ALL early returns to maintain stable hook count
  const hasConfiguredSource = hasConfiguredAutomationInput(config);
  const isConfigured = hasConfiguredSource && !!config?.workflowId;

  // sl-1ahs.6.4: Cached execution-response state removed together with the
  // Test Run infrastructure (sl-1ahs.7.2). Output mappings are now derived
  // from the workflow End node output schema (sl-1ahs.7.3) instead of from
  // a remembered response object, so the panel no longer needs to subscribe
  // to execution logs or persist anything onto blockModel.props.

  // Get ALL input table columns for resolving column IDs to names in response preview
  // sl-i6t9-fix: Include source + linked/secondary table columns so that linked table
  // column IDs (e.g. btki6AZb2U) are resolved to human-readable names in field mapping.
  const sourceColumns = useMemo((): ColumnInfo[] => {
    if (!config?.sourceTableId) return [];
    const allColumns: ColumnInfo[] = [];
    const seenIds = new Set<string>();

    // Source table columns
    const sourceDb = databases.find(
      (db: { blockId: string }) => db.blockId === config.sourceTableId
    );
    for (const col of sourceDb?.columns ?? []) {
      if (!seenIds.has(col.id)) {
        allColumns.push(col);
        seenIds.add(col.id);
      }
    }

    // Linked/secondary input table columns
    for (const inputTable of config.inputTables ?? []) {
      if (!inputTable.tableId || inputTable.tableId === config.sourceTableId)
        continue;
      const db = databases.find(
        (d: { blockId: string }) => d.blockId === inputTable.tableId
      );
      for (const col of db?.columns ?? []) {
        if (!seenIds.has(col.id)) {
          allColumns.push(col);
          seenIds.add(col.id);
        }
      }
    }

    return allColumns;
  }, [config?.sourceTableId, config?.inputTables, databases]);

  // Invalidate cached response structure when the linked workflow is re-published
  // (output schema may have changed — stale mappings would reference non-existent fields)
  useEffect(() => {
    let disposed = false;
    const handleWorkflowPublished = (event: Event) => {
      const { workflowId } = (event as CustomEvent<{ workflowId: string }>)
        .detail;
      if (
        !blockModel ||
        !config?.workflowId ||
        config.workflowId !== workflowId
      )
        return;

      workflowService.invalidateSchemaCache(workflowId);
      workflowService.selectedWorkflowSchema$.next(null);
      // Re-fetch schema immediately so sidebar updates without page refresh
      void workflowService.loadWorkflowSchema(workflowId).catch(error => {
        if (!disposed)
          notify.warning({
            title: t['com.streamlain.streamlain.input.schema-load-failed'](),
            message: error instanceof Error ? error.message : String(error),
          });
      });

      // Refresh the compact canvas projection only for the active automation.
      // Other canvas blocks remain entirely local-first and make no requests.
      workflowService.invalidateWorkflowStepSummaries(workflowId);
      void workflowService
        .loadWorkflowStepSummaries(workflowId, true)
        .then(workflowSteps => {
          if (disposed || !currentDoc || !blockModel) return;
          const props = blockModel.props as Record<string, unknown>;
          if (props.workflowId !== workflowId) return;
          currentDoc.transact(() => {
            props.workflowSteps = workflowSteps;
            props.workflowStepsSourceId = workflowId;
          });
        })
        .catch(error => {
          console.error(
            '[AutomationPanel] Failed to refresh workflow-step cache:',
            error
          );
        });
    };

    // When workflow is renamed, update block title + workflowName prop
    const handleWorkflowRenamed = (event: Event) => {
      const { workflowId, name: newName } = (
        event as CustomEvent<{ workflowId: string; name: string }>
      ).detail;
      if (
        !blockModel ||
        !config?.workflowId ||
        config.workflowId !== workflowId
      )
        return;
      writeAutomationTitle(
        blockModel as { props: Record<string, unknown> } | null,
        newName,
        'workflow'
      );
      const props = blockModel.props as Record<string, unknown>;
      props.workflowName = newName;
    };

    window.addEventListener('workflow:published', handleWorkflowPublished);
    window.addEventListener('workflow:renamed', handleWorkflowRenamed);
    return () => {
      disposed = true;
      window.removeEventListener('workflow:published', handleWorkflowPublished);
      window.removeEventListener('workflow:renamed', handleWorkflowRenamed);
    };
  }, [blockModel, config?.workflowId, currentDoc, workflowService, t]);

  // E018-S008-T005: Sync database names when they change (CrossDoc reactive updates)
  // This keeps sourceTableName and targetTableName in sync with actual database titles
  useEffect(() => {
    if (!blockModel || !currentDoc || !config) return;

    const props = blockModel.props as Record<string, unknown>;
    let needsUpdate = false;
    // sl-4n0: Changed from Record<string, string> to support outputTables array
    const updates: Record<string, unknown> = {};

    // sl-1ahs.8.1: Webhook triggers may also have a source table (sl-1ahs.2.1
    // removed the UI guards), so name-sync must apply for all trigger types.
    if (config.sourceTableId) {
      const sourceDb = databases.find(
        (db: { blockId: string }) => db.blockId === config.sourceTableId
      );
      if (sourceDb && sourceDb.databaseTitle !== config.sourceTableName) {
        updates.sourceTableName = sourceDb.databaseTitle;
        needsUpdate = true;
      }
    }

    // sl-4n0: Check output table names
    if (config.outputTables && config.outputTables.length > 0) {
      const updatedOutputTables = config.outputTables.map(outputTable => {
        const targetDb = databases.find(
          (db: { blockId: string }) => db.blockId === outputTable.tableId
        );
        if (targetDb && targetDb.databaseTitle !== outputTable.tableName) {
          needsUpdate = true;
          return { ...outputTable, tableName: targetDb.databaseTitle };
        }
        return outputTable;
      });
      if (needsUpdate) {
        updates.outputTables = updatedOutputTables;
      }
    }

    // Apply updates if needed
    if (needsUpdate) {
      currentDoc.transact(() => {
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });
    }
  }, [blockModel, currentDoc, config, currentDocId, databases]);

  // Keep stored doc IDs in sync when a referenced database is discovered in a
  // different doc. Missing entries in database discovery are intentionally not
  // treated as deletion: discovery is local/transient during sync, reload, and
  // execution refreshes. Nulling persisted config from that signal can destroy
  // valid source/output references.
  useEffect(() => {
    if (!blockModel || !currentDoc || !config) return;

    const props = blockModel.props as Record<string, unknown>;
    const updates = computeDatabaseReferenceMoveUpdates(
      config,
      databases,
      currentDocId
    );

    // Apply updates if any database was moved
    if (updates) {
      currentDoc.transact(() => {
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });
    }
  }, [blockModel, currentDoc, config, currentDocId, databases]);

  // Get target columns for Output Tab mapping (single table mode)
  // sl-trm: Read mode from first outputTable (per-table mode, no global legacy)
  const targetColumns = useMemo((): ColumnInfo[] => {
    if (!config) return [];

    const primaryOutputTable = config.outputTables?.[0];

    // For update_source mode, use source table columns
    if (primaryOutputTable?.mode === 'update_source') {
      const sourceDb = databases.find(
        (db: { blockId: string }) => db.blockId === config.sourceTableId
      );
      return sourceDb?.columns ?? [];
    }

    // For append/other modes, use primary output table columns
    if (primaryOutputTable) {
      const targetDb = databases.find(
        (db: { blockId: string }) => db.blockId === primaryOutputTable.tableId
      );
      return targetDb?.columns ?? [];
    }

    return [];
  }, [config, databases]);

  // sl-3nk: Map of tableId -> columns for multi-table field mapping
  const tableColumnsMap = useMemo((): Map<string, ColumnInfo[]> => {
    const map = new Map<string, ColumnInfo[]>();
    if (!config?.outputTables) return map;

    for (const outputTable of config.outputTables) {
      if (!outputTable.tableId) continue;
      const db = databases.find(
        (d: { blockId: string }) => d.blockId === outputTable.tableId
      );
      if (db?.columns) {
        map.set(outputTable.tableId, db.columns);
      }
    }
    return map;
  }, [config?.outputTables, databases]);

  // S020-8.20.6: Get columns used by other automations for conflict detection
  const usedColumns = useMemo(() => {
    if (!activeBlockId || !config?.sourceTableId) {
      return new Map();
    }
    return automationDiscoveryService.getUsedColumns(
      activeBlockId,
      config.sourceTableId
    );
  }, [activeBlockId, config?.sourceTableId, automationDiscoveryService]);

  // S020-8.20.6: Navigate to conflicting automation with cinematic effect
  const handleNavigateToConflict = useCallback(
    async (automationId: string, _docId: string) => {
      // Find the automation to get its position
      const automation =
        automationDiscoveryService.findAutomation(automationId);
      if (!automation || !automation.xywh) {
        console.warn(
          '[AutomationPanel] Cannot navigate to conflict - automation not found or no position'
        );
        return;
      }

      // Parse xywh to get position
      const [x, y] = automation.xywh.split(',').map(Number);
      if (isNaN(x) || isNaN(y)) {
        console.warn('[AutomationPanel] Invalid xywh:', automation.xywh);
        return;
      }

      // Get viewport for cinematic navigation
      const edgelessRoot = document.querySelector('affine-edgeless-root');
      const gfx = (edgelessRoot as any)?.gfx;

      if (gfx?.viewport) {
        // Import dynamically to avoid circular deps
        const { cinematicNavigateTo } =
          await import('../utils/cinematic-viewport');
        await cinematicNavigateTo(gfx.viewport, x, y, {
          duration: 1000,
          minZoom: 0.75,
        });

        // Set the automation as active to open its sidebar
        automationService.setActiveBlock(automationId);
      }
    },
    [automationDiscoveryService, automationService]
  );

  // sl-trigger-removal.5: Trigger sync useEffect removed - scheduling now handled via ConfigService/ScheduleService

  // E018-S007-T001: Update BlockModel props directly
  // NO setConfig needed - useBlockModelConfig hook auto-updates via propsUpdated subscription
  const handleUpdate = useCallback(
    (updates: Partial<AutomationConfig>) => {
      if (!blockModel || !currentDoc) {
        console.warn('[AutomationPanel] No blockModel or currentDoc available');
        return;
      }
      // sl-za0v.10: RBAC — block config edits without canEdit permission
      if (!wfPermissions.canEdit) {
        console.warn(
          '[AutomationPanel] Config update blocked by RBAC (canEdit=false)'
        );
        return;
      }
      currentDoc.transact(() => {
        const props = blockModel.props as Record<string, unknown>;

        if (props.executionKind === 'cleanup') {
          const output = updates.outputTables?.[0];
          const databaseId = updates.sourceTableId ?? output?.tableId;
          if (databaseId) {
            const docId = updates.sourceDocId ?? output?.docId ?? currentDoc.id;
            updates = {
              ...updates,
              ...cleanupBindingUpdates(
                blockModel as AutomationBlockModel,
                { workspaceId: workspace.id, docId, databaseId },
                output ? 'output' : 'input',
                updates.sourceTableName ?? output?.tableName ?? ''
              ),
              triggerConfig: {
                ...(props.triggerConfig as AutomationConfig['triggerConfig']),
                enabled: false,
              } as AutomationConfig['triggerConfig'],
            };
          } else if (
            updates.sourceTableId === null ||
            updates.outputTables?.length === 0
          ) {
            updates = {
              ...updates,
              ...cleanupBindingUpdates(
                blockModel as AutomationBlockModel,
                null,
                'input'
              ),
              triggerConfig: {
                ...(props.triggerConfig as AutomationConfig['triggerConfig']),
                enabled: false,
              } as AutomationConfig['triggerConfig'],
            };
          }
          if (
            updates.sourceTableId !== undefined ||
            updates.outputTables !== undefined ||
            updates.filterGroup !== undefined
          ) {
            updates = {
              ...updates,
              triggerConfig: {
                ...(props.triggerConfig as AutomationConfig['triggerConfig']),
                enabled: false,
              } as AutomationConfig['triggerConfig'],
            };
          }
        }

        // Decide ownership against the previous workflow identity.
        if (updates.workflowName !== undefined) {
          writeAutomationTitle(
            blockModel as { props: Record<string, unknown> } | null,
            (updates.workflowName as string) || '',
            'workflow'
          );
        }

        // Apply updates
        for (const [key, value] of Object.entries(updates)) {
          props[key] = value;
        }
      });

      // Backend sync on workflowId change is handled at editor level
      // (blocksuite-editor.tsx blockUpdated listener) — fires for ALL assignment
      // paths (sidebar, workflow builder, programmatic).
      // E018-S007-T001: No setConfig needed - hook auto-re-renders via propsUpdated
    },
    [blockModel, currentDoc, wfPermissions.canEdit, workspace.id]
  );

  // Without the workflow plan feature a webhook trigger cannot run; fall back
  // to manual once the plan is known.
  useEffect(() => {
    if (
      !config ||
      isFeatureGateLoading ||
      hasWorkflowFeature ||
      config.triggerConfig?.type !== 'webhook'
    )
      return;
    handleUpdate({
      triggerConfig: {
        ...config.triggerConfig,
        type: 'manual',
        enabled: false,
        webhookCredentialId: null,
      },
    });
  }, [config, handleUpdate, hasWorkflowFeature, isFeatureGateLoading]);

  useWorkflowStepCache(config, handleUpdate);

  const readCurrentMappings = useCallback<
    WorkflowSwitchDeps['readMappings']
  >(() => {
    const current = extractConfig(blockModel);
    return {
      currentInputs: current?.inputMappings ?? [],
      currentOutputs: current?.outputMappings ?? [],
    };
  }, [blockModel]);

  // ── Readiness: one model for the start page, the tile grid and the locks ──
  const missingAiKeys = useAiKeyCheck(
    configApiService,
    activeBlockId,
    workspace.id,
    config?.workflowId
  );
  const webhooks = useInboundWebhooks(
    workspace.id,
    hasWorkflowFeature && config?.triggerConfig?.type === 'webhook'
  );
  const webhookName =
    webhooks.find(
      webhook =>
        webhook.credentialId === config?.triggerConfig?.webhookCredentialId
    )?.name ?? null;
  const logs = useLiveData(
    activeBlockId ? loggingService.getLogs$(activeBlockId) : null
  );
  const activeLog = useLiveData(
    activeBlockId ? loggingService.getActiveLog$(activeBlockId) : null
  );
  const lastRun = useMemo(
    () =>
      [activeLog, ...(logs ?? [])]
        .filter((log): log is ExecutionLog => !!log)
        .sort((a, b) => b.startedAt - a.startedAt)[0] ?? null,
    [activeLog, logs]
  );
  const activeTestRun = useLiveData(workflowTestContext.activeRun$);
  const hasTestContext =
    !!activeTestRun &&
    activeTestRun.run.automationBlockId === activeBlockId &&
    activeTestRun.run.workflowId === config?.workflowId;
  const safetyPresentation = useMemo(
    () =>
      safetyDiagnostic
        ? presentAutomationSafety(
            safetyDiagnostic.topology.state,
            safetyDiagnostic.actions
          )
        : null,
    [safetyDiagnostic]
  );
  const statusColumnCandidates = useMemo(
    () =>
      (
        databases.find(
          (db: { blockId: string }) => db.blockId === config?.sourceTableId
        )?.columns ?? []
      ).filter(column => column.type === 'select'),
    [config?.sourceTableId, databases]
  );
  const sourceIsRowless = useMemo(() => {
    const source = databases.find(db => db.blockId === config?.sourceTableId);
    return !!source && isMailFolderTable(source);
  }, [config?.sourceTableId, databases]);
  const readinessFacts = useMemo<ReadinessFacts>(
    () => ({
      workflow: selectedWorkflow ? { active: !!selectedWorkflow.active } : null,
      schema: workflowSchema,
      sourceIsRowless,
      hasStatusColumnCandidate: statusColumnCandidates.length > 0,
      statusColumnShared,
      missingAiKeys,
      hasTestContext,
      safety: safetyPresentation
        ? {
            severity: safetyPresentation.severity,
            titleKey: safetyPresentation.titleKey,
          }
        : null,
    }),
    [
      hasTestContext,
      missingAiKeys,
      safetyPresentation,
      selectedWorkflow,
      sourceIsRowless,
      statusColumnCandidates.length,
      statusColumnShared,
      workflowSchema,
    ]
  );
  const readiness = useMemo(
    () =>
      config
        ? computeAutomationReadiness({
            config,
            ...readinessFacts,
            lastRun: lastRun
              ? {
                  status: lastRun.status,
                  terminalReason: lastRun.terminalReason,
                }
              : null,
          })
        : null,
    [config, lastRun, readinessFacts]
  );
  // The canvas block evaluates the same facts, so it shows the same status.
  const readinessWorkflowId = config?.workflowId ?? null;
  useEffect(() => {
    if (!activeBlockId || !config) return;
    setAutomationReadinessFacts(activeBlockId, {
      ...readinessFacts,
      workflowId: readinessWorkflowId,
    });
  }, [activeBlockId, config, readinessFacts, readinessWorkflowId]);

  const handleCreateStatusColumn = useCallback(async () => {
    if (!config?.sourceTableId || !currentDoc) {
      notify.error({
        title: t['com.streamlain.streamlain.execution.missing-source'](),
      });
      return;
    }
    if (!wfPermissions.canEdit) {
      notify.error({
        title: t['com.streamlain.no-permission'](),
      });
      return;
    }

    const sourceDocId = config.sourceDocId || currentDocId;
    let releaseSourceDoc: (() => void) | undefined;

    try {
      let sourceDoc = currentDoc;
      if (sourceDocId && sourceDocId !== currentDocId) {
        const opened = docsService.open(sourceDocId);
        releaseSourceDoc = opened.release;
        sourceDoc = opened.doc.blockSuiteDoc;
        sourceDoc.load();
      }

      const sourceBlock = sourceDoc.getBlock(config.sourceTableId);
      const sourceModel = sourceBlock?.model;
      if (!sourceModel) {
        throw new Error('Source database block not found.');
      }

      const columnName =
        config.statusTracking?.createColumn?.name?.trim() ||
        DEFAULT_STATUS_TRACKING_CONFIG.createColumn?.name ||
        'Status';
      const standardOptions = config.statusTracking?.standardOptions?.length
        ? config.statusTracking.standardOptions
        : DEFAULT_STATUS_TRACKING_CONFIG.standardOptions;
      const columnId = addSourceTableColumn(
        sourceDoc,
        sourceModel,
        { type: 'select', name: columnName },
        { options: createSelectColumnOptions(standardOptions) }
      );

      handleUpdate({
        statusTracking: {
          ...DEFAULT_STATUS_TRACKING_CONFIG,
          ...config.statusTracking,
          enabled: true,
          mode: 'status_column',
          columnId,
          standardOptions,
        },
      });

      notify.success({
        title: columnName,
      });
    } catch (err) {
      console.error('[AutomationPanel] Status column creation failed:', err);
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.validation.status-tracking-incomplete'
          ](),
        message: t['com.streamlain.automation.sidebar.status-column-failed'](),
      });
    } finally {
      releaseSourceDoc?.();
    }
  }, [
    config,
    currentDoc,
    currentDocId,
    docsService,
    handleUpdate,
    t,
    wfPermissions.canEdit,
  ]);

  // "Add relation" in the input settings: only for a source table on this
  // canvas or page, where its column menu can open.
  const sourceTableModel =
    config?.sourceTableId &&
    (!config.sourceDocId || config.sourceDocId === currentDocId)
      ? currentDoc?.getBlock(config.sourceTableId)?.model
      : undefined;
  const handleAddRelationColumn = useCallback(() => {
    if (!sourceTableModel || !currentDoc) return;
    if (!wfPermissions.canEdit) {
      notify.error({ title: t['com.streamlain.no-permission']() });
      return;
    }
    addRelationColumn(
      currentDoc,
      sourceTableModel,
      t['com.streamlain.dataview.property.type.relation']()
    )
      .then(opened => {
        if (!opened)
          notify({
            title: t['com.streamlain.automation.panel.related.needs-table'](),
          });
      })
      .catch(error => {
        console.error('[AutomationPanel] Relation column failed:', error);
        notify.error({
          title: t['com.streamlain.automation.panel.related.add-failed'](),
        });
      });
  }, [currentDoc, sourceTableModel, t, wfPermissions.canEdit]);

  // Stale workflow cleanup handled at block level via workflow:deleted event
  // (automation-canvas-block.ts) — no sidebar dependency needed.

  // sl-trigger-explicit: Handle schedule toggle - saves/disables schedule on backend
  const handleScheduleToggle = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      if (
        !activeBlockId ||
        !automationInfo?.docId ||
        !workspace?.id ||
        !config
      ) {
        console.warn('[AutomationPanel] Missing context for schedule toggle');
        return false;
      }

      setIsScheduleSaving(true);
      const trigger = config.triggerConfig;
      const schedule = {
        triggerType: trigger?.type ?? 'scheduled',
        cronPreset: trigger?.cronPreset,
        timezone: trigger?.timezone,
        // Named with the request, so the switch does not wait for the doc
        // sync to bind the webhook first.
        ...(trigger?.type === 'webhook'
          ? { webhookCredentialId: trigger.webhookCredentialId ?? null }
          : {}),
      };

      try {
        if (enabled) {
          // Step 1: Validate workflow is selected
          const workflowId = config.workflowId;
          if (!workflowId) {
            console.error('[AutomationPanel] No workflow selected');
            return false;
          }

          // Step 2: Flush the same page/root/row-shard document set used by
          // the execution preflight. A schedule can fire immediately after
          // activation, so persisting only the automation config allows its
          // first tick to observe an empty source database.
          await flushPreFlightDocs(
            workspace.engine.doc,
            {
              automationDocId: automationInfo.docId,
              config,
              profile: 'execute',
              databaseStorageRefs: databases.map(database => ({
                tableId: database.blockId,
                docId: database.docId,
                databaseRootDocId: database.databaseRootDocId,
                databaseDirectoryPartDocIds:
                  database.databaseDirectoryPartDocIds,
                databaseRowShardDocIds: database.databaseRowShardDocIds,
              })),
              workspaceId: workspace.id,
            },
            preFlightLocalDocs(workspace),
            preFlightDatabaseDirectories(databaseDiscoveryService, docsService)
          );

          // Step 3: Save config first (required before schedule can be created)
          // Backend resolves webhook URL from workflow via Y.js sync
          const savedConfig = await configApiService.saveConfig(
            activeBlockId,
            workspace.id,
            {
              docId: automationInfo.docId,
            }
          );

          if (!savedConfig) {
            console.error('[AutomationPanel] Failed to save config');
            return false;
          }

          // Step 4: Persist schedule configuration and activation together.
          // The backend owns the authoritative topology projection and enable
          // decision. Polling the same boundary here first made activation
          // wait through the projection twice. A refusal reaches the notice
          // below instead of leaving the switch off without a word.
          const scheduleResult = await configApiService.requestSchedule(
            activeBlockId,
            workspace.id,
            automationInfo.docId,
            { ...schedule, enabled: true }
          );

          window.__automationRuntimeBridge?.setSchedule(
            activeBlockId,
            scheduleResult
          );

          return true;
        } else {
          const scheduleResult = await configApiService.saveSchedule(
            activeBlockId,
            workspace.id,
            automationInfo.docId,
            { ...schedule, enabled: false }
          );
          if (!scheduleResult) return false;
          window.__automationRuntimeBridge?.setSchedule(
            activeBlockId,
            scheduleResult
          );
          return true;
        }
      } catch (error) {
        if (isAutomationTakeoverRequired(error)) {
          setTakeoverOpen(true);
          return false;
        }
        console.error('[AutomationPanel] Schedule toggle error:', error);
        notify.error({
          title: t['com.streamlain.automation-safety.enable-denied'](),
          message: t[automationSafetyEnableFailureKey(error)](),
        });
        return false;
      } finally {
        setIsScheduleSaving(false);
      }
    },
    [
      activeBlockId,
      automationInfo?.docId,
      workspace,
      config,
      configApiService,
      databases,
      databaseDiscoveryService,
      docsService,
      t,
    ]
  );

  const handleHeaderScheduleToggle = useCallback(async () => {
    if (!config || !readiness) return;
    if (!wfPermissions.canEdit) {
      notify.warning({
        title:
          t['com.streamlain.streamlain.execution.notify.edit-denied-title'](),
        message:
          t['com.streamlain.streamlain.execution.notify.edit-denied-message'](),
      });
      return;
    }

    const newEnabled = !config.triggerConfig?.enabled;
    if (newEnabled && !readiness.canActivate) {
      // Point at the first gap instead of failing on the backend.
      const gap =
        readiness.issues.find(issue => issue.severity === 'missing') ??
        readiness.issues[0];
      notify.error({
        title:
          t['com.streamlain.streamlain.validation.cannot-activate-trigger'](),
        message: gap
          ? t.t(gap.labelKey)
          : t['com.streamlain.automation.sidebar.activate-manual'](),
      });
      if (gap) setSettingsTile(gap.tile);
      return;
    }

    const success = await handleScheduleToggle(newEnabled);
    if (!success) return;

    handleUpdate({
      triggerConfig: {
        type: config.triggerConfig?.type || 'scheduled',
        cronPreset: config.triggerConfig?.cronPreset || 'hourly',
        ...config.triggerConfig,
        enabled: newEnabled,
      },
    });
    const notification = {
      title: newEnabled
        ? t['com.streamlain.streamlain.process.schedule-active-info']()
        : t['com.streamlain.streamlain.process.schedule-inactive-info'](),
    };
    if (newEnabled) {
      notify.success(notification);
    } else {
      notify.warning(notification);
    }
  }, [
    config,
    handleScheduleToggle,
    handleUpdate,
    readiness,
    t,
    wfPermissions.canEdit,
  ]);

  const handleDeactivateSchedule = useCallback(
    () => handleScheduleToggle(false),
    [handleScheduleToggle]
  );

  // A local config readback confirms the target AND every mapping. This is
  // deliberately not a server-sync acknowledgement.
  useEffect(() => {
    const pending = pendingWebhookInbox.current;
    if (!pending) return;
    if (pending.blockId !== blockModel?.id) {
      pendingWebhookInbox.current = null;
      setIsStoringWebhook(false);
      return;
    }
    const result = pending.result;
    if (
      !result ||
      !config?.outputTables?.some(
        table =>
          table.tableId === result.outputTable.tableId &&
          table.docId === result.outputTable.docId
      ) ||
      !result.outputMappings.every(expected =>
        config.outputMappings?.some(
          mapping =>
            mapping.tableId === expected.tableId &&
            mapping.targetColumnId === expected.targetColumnId &&
            mapping.responseField === expected.responseField
        )
      )
    )
      return;
    pendingWebhookInbox.current = null;
    setIsStoringWebhook(false);
    notify.success({
      title: t['com.streamlain.automation.sidebar.webhook-table-created'](),
    });
  }, [blockModel?.id, config, isStoringWebhook, t]);

  // Webhook "Store in table": creates the incoming table next to the block
  // from the workflow's input fields and sets it as output, fields mapped.
  const storeWebhookInTable = useCallback(async () => {
    if (
      !config ||
      !blockModel ||
      !currentDoc ||
      !workflowSchema ||
      !wfPermissions.canEdit ||
      !hasWorkflowFeature ||
      config.triggerConfig?.type !== 'webhook' ||
      pendingWebhookInbox.current
    )
      return;
    pendingWebhookInbox.current = { blockId: blockModel.id, result: null };
    setIsStoringWebhook(true);
    try {
      const anchor = currentDoc.getBlock(blockModel.id)?.model;
      if (!anchor) {
        throw new Error('The automation block is not in the open document.');
      }
      const { outputTable, outputMappings } = await createWebhookInboxTable({
        doc: currentDoc,
        docId: currentDoc.id,
        anchor,
        title: t.t('com.streamlain.automation.sidebar.webhook-table-name', {
          name:
            webhookName ??
            t['com.streamlain.automation.sidebar.trigger.webhook'](),
        }),
        schema: workflowSchema,
      });
      pendingWebhookInbox.current.result = { outputTable, outputMappings };
      handleUpdate({
        outputTables: [
          ...(config.outputTables ?? []).filter(table => table.tableId),
          outputTable,
        ],
        outputMappings: [...(config.outputMappings ?? []), ...outputMappings],
      });
    } catch (error) {
      pendingWebhookInbox.current = null;
      setIsStoringWebhook(false);
      console.error('[AutomationPanel] Webhook table creation failed:', error);
      notify.error({
        title: t['com.streamlain.automation.sidebar.webhook-table-failed'](),
        ...(error instanceof WebhookInboxMappingError
          ? {
              message: t.t(
                'com.streamlain.automation.sidebar.webhook-table-mapping-required'
              ),
            }
          : {}),
      });
    }
  }, [
    blockModel,
    config,
    currentDoc,
    handleUpdate,
    t,
    webhookName,
    workflowSchema,
    wfPermissions.canEdit,
    hasWorkflowFeature,
  ]);
  const handleStoreWebhookInTable = useCallback(() => {
    void storeWebhookInTable();
  }, [storeWebhookInTable]);

  const executionRows = useMemo(
    () => processedRows?.payloads ?? [],
    [processedRows?.payloads]
  );

  // E024-S001-T001: Local Storage Restriction - Show Cloud-Only Message
  // This blocks ALL automation configuration in Local Storage workspaces
  if (isLocalStorage) {
    const buttonLabel = isLoggedIn
      ? t['Enable AFFiNE Cloud']()
      : t['Sign in and Enable']();

    return (
      <div className={styles.panel}>
        <div className={startStyles.cloudGate}>
          <h3 className={startStyles.cloudGateTitle}>
            {t.t('com.streamlain.automation.sidebar.cloud-required.title', {
              brand: BRAND_DISPLAY_NAME,
            })}
          </h3>
          <p className={startStyles.cloudGateBody}>
            {t.t('com.streamlain.automation.sidebar.cloud-required.body', {
              brand: BRAND_DISPLAY_NAME,
            })}
          </p>
          <Button variant="secondary" onClick={handleEnableCloud}>
            {buttonLabel}
          </Button>
        </div>
      </div>
    );
  }

  // Row menu "Automation runs": the runs of one table row.
  if (rowHistoryTarget) {
    return (
      <div className={styles.panel}>
        <RowHistoryPanel
          target={rowHistoryTarget}
          onClose={() => automationService.setRowHistoryTarget(null)}
        />
      </div>
    );
  }

  // Empty state - no automation block selected, show workspace overview
  if (!activeBlockId || !config || !readiness) {
    return (
      <div className={styles.panel}>
        <WorkspaceAutomationsPanel />
      </div>
    );
  }

  if (config.executionKind === 'cleanup') {
    return <CleanupPanel blockId={activeBlockId} />;
  }

  // ── Summaries for the steps and the tiles ──
  const trigger = config.triggerConfig?.type ?? 'manual';
  const isWebhook = trigger === 'webhook';
  const activatable = isActivatableTrigger(trigger);
  const enabled = activatable && !!config.triggerConfig?.enabled;
  const workflowName = selectedWorkflow?.name ?? config.workflowName ?? '';
  const stateLabel = (tile: ReadinessTileId) =>
    t.t(`com.streamlain.automation.panel.state.${readiness.tiles[tile]}`);
  const secondaries = (config.inputTables ?? []).filter(
    table => table.role === 'secondary'
  );
  const filterCount = config.filterGroup?.conditions?.length ?? 0;
  const targets = (config.outputTables ?? []).filter(table => table.tableId);
  const targetNames = targets
    .map(table => table.tableName)
    .filter(Boolean)
    .join(', ');
  const batchConfig = normalizeBatchConfig(config.batchConfig);
  const mappingInput = workflowSchema
    ? mappableInputs(workflowSchema.input)
    : null;
  const inputFieldCount = mappingInput
    ? mappingInput.required.length + mappingInput.optional.length
    : 0;
  const outputFieldCount = workflowSchema
    ? Object.keys(workflowSchema.output).length
    : 0;
  const trackingColumn = statusColumnCandidates.find(
    column => column.id === config.statusTracking?.columnId
  );
  const fanoutField = targets.find(
    table => table.sourceArrayField
  )?.sourceArrayField;
  const writeModeLabels = [
    ...new Set(
      targets.map(table => {
        const match = (config.matchConfigs ?? []).find(
          entry => entry.id === table.matchConfigId
        );
        const mode = !match?.keys.length
          ? 'append'
          : match.notFoundBehavior === 'fail'
            ? 'update_by_match'
            : 'upsert';
        return t.t(`com.streamlain.automation.overview.output-mode.${mode}`);
      })
    ),
  ];

  const sourceLabel = isWebhook
    ? webhookName
      ? t.t('com.streamlain.automation.sidebar.source-webhook', {
          name: webhookName,
        })
      : stateLabel('source')
    : config.sourceTableId
      ? config.sourceTableName || stateLabel('source')
      : t['com.streamlain.automation.sidebar.source-none']();

  const tileValues: Record<ReadinessTileId, string> = {
    source: sourceLabel,
    related:
      secondaries.length > 0
        ? t.t('com.streamlain.automation.sidebar.related-count', {
            count: secondaries.length,
          })
        : stateLabel('related'),
    filter:
      filterCount > 0
        ? t.t('com.streamlain.automation.sidebar.filter-summary', {
            count: filterCount,
          })
        : stateLabel('filter'),
    tracking:
      config.statusTracking?.enabled &&
      config.statusTracking.mode === 'delete_row'
        ? t.t('com.streamlain.streamlain.tracking.mode-delete-row')
        : config.statusTracking?.enabled && trackingColumn
          ? t.t('com.streamlain.automation.sidebar.tracking-summary', {
              value: trackingColumn.name,
            })
          : stateLabel('tracking'),
    rows: !config.sourceTableId
      ? stateLabel('rows')
      : batchConfig.executionMode === 'batch'
        ? t.t('com.streamlain.automation.panel.rows.all-summary', {
            count: batchConfig.batchSize,
          })
        : t.t('com.streamlain.automation.panel.rows.next'),
    workflow: workflowName || stateLabel('workflow'),
    mapping:
      inputFieldCount > 0
        ? t.t('com.streamlain.automation.sidebar.mapped-count', {
            mapped: (config.inputMappings ?? []).filter(
              mapping => mapping.columnId
            ).length,
            total: inputFieldCount,
          })
        : stateLabel('mapping'),
    test: stateLabel('test'),
    failure: t.t('com.streamlain.automation.sidebar.failure-summary', {
      mode: t.t(
        `com.streamlain.automation.sidebar.failure.${batchConfig.onFailure}`
      ),
      retry: t.t(
        batchConfig.retry.enabled
          ? 'com.streamlain.automation.sidebar.failure.retry-on'
          : 'com.streamlain.automation.sidebar.failure.retry-off'
      ),
    }),
    target:
      targetNames ||
      (readiness.tiles.target === 'none'
        ? t.t('com.streamlain.automation.panel.no-output')
        : stateLabel('target')),
    fields:
      outputFieldCount > 0
        ? t.t('com.streamlain.streamlain.process.canvas.columns-mapped', {
            count: String(
              (config.outputMappings ?? []).filter(
                mapping =>
                  mapping.responseField || mapping.fixedValue !== undefined
              ).length
            ),
          })
        : stateLabel('fields'),
    match:
      writeModeLabels.length > 0
        ? writeModeLabels.join(' · ')
        : stateLabel('match'),
    fanout: fanoutField
      ? t.t('com.streamlain.automation.sidebar.fanout-summary', {
          field: fanoutField,
        })
      : t['com.streamlain.automation.sidebar.fanout-none'](),
    trigger: t.t(`com.streamlain.automation.sidebar.trigger.${trigger}`),
    history: lastRun
      ? formatHistoryExecutionStatusLabel(t, lastRun.status)
      : t.t('com.streamlain.automation.overview.no-last-run'),
  };

  const resolveIssue = (issue: ReadinessIssue) => {
    switch (issue.action) {
      case 'create-status-column':
        void handleCreateStatusColumn();
        return;
      case 'open-builder':
        if (config.workflowId) {
          openWorkflowBuilder(
            editorService?.editor,
            config.workflowId,
            workflowName
          );
        }
        return;
      default:
        setSettingsTile(issue.tile);
        setAttentionTile(issue.tile);
    }
  };

  const hubShareState: HubShareState = !publishAllowed
    ? 'not-in-plan'
    : !canPublishMarketplace
      ? 'no-role'
      : canPublishAutomationTemplate
        ? 'ready'
        : 'needs-run';

  /** A card opens its pane at its first gap; a second click closes it. */
  const togglePane = (pane: SettingsPane) =>
    setSettingsTile(current =>
      current !== null && paneOfTile(current) === pane
        ? null
        : (readiness.issues.find(issue => paneOfTile(issue.tile) === pane)
            ?.tile ?? PANE_PRIMARY_TILE[pane])
    );

  const firstRunBlocker = readiness.issues.find(
    issue => issue.severity === 'missing' && issue.blocksRun
  );

  // Only a trigger that runs by itself is switched on and off. A manual
  // automation has nothing to activate, so it shows no switch.
  const activationSwitch = activatable ? (
    <Switch
      className={startStyles.activation}
      size={20}
      padding={2}
      checked={enabled}
      disabled={!wfPermissions.canEdit}
      onChange={() => {
        if (!isScheduleSaving) void handleHeaderScheduleToggle();
      }}
      aria-label={
        enabled
          ? t['com.streamlain.automation.sidebar.pause']()
          : t['com.streamlain.automation.sidebar.activate']()
      }
      title={
        enabled
          ? t['com.streamlain.automation.sidebar.pause']()
          : readiness.canActivate
            ? t['com.streamlain.automation.sidebar.activate']()
            : t['com.streamlain.automation.sidebar.activate-locked']()
      }
      data-on={enabled || undefined}
      data-busy={isScheduleSaving || undefined}
      data-coachmark-target="trigger-enable-toggle"
      data-testid="automation-activation"
    >
      {t['com.streamlain.automation.panel.tile.trigger']()}
    </Switch>
  ) : null;

  const executionProps = {
    blockId: activeBlockId,
    isConfigured,
    rows: executionRows,
    processedRows,
    workflow: selectedWorkflow,
    config,
    accessibleDatabases: databases,
    canExecute: wfPermissions.canRun,
  };

  const triggerSettings = (
    <TriggerSettings
      config={config}
      onUpdate={handleUpdate}
      onScheduleToggle={handleScheduleToggle}
      isScheduleSaving={isScheduleSaving}
      canEdit={wfPermissions.canEdit}
      webhookAllowed={hasWorkflowFeature}
      webhooks={webhooks}
      onStoreWebhookInTable={handleStoreWebhookInTable}
      workspaceId={workspace.id}
      blockId={activeBlockId ?? null}
      sourceColumns={sourceColumns}
      onOpenFilter={openFilterSetting}
      canStoreWebhookInTable={
        wfPermissions.canEdit &&
        !isStoringWebhook &&
        !!workflowSchema &&
        inputFieldCount > 0
      }
    />
  );

  const inputProps = {
    config,
    onUpdate: handleUpdate,
    sourceColumns,
    accessibleDatabases: databases,
    usedColumns,
    onNavigateToConflict: (automationId: string, docId: string) => {
      handleNavigateToConflict(automationId, docId).catch(console.error);
    },
    onCreateStatusColumn: handleCreateStatusColumn,
    onDeactivateSchedule: handleDeactivateSchedule,
    webhookName,
    onAddRelationColumn: sourceTableModel ? handleAddRelationColumn : undefined,
  };

  const settingsGroups = {
    input: <InputTab part="input" {...inputProps} />,
    workflow: (
      <>
        <SettingUnit name="workflow" tiles={WORKFLOW_UNIT_TILES}>
          <ProcessTab
            part="workflow"
            config={config}
            onUpdate={handleUpdate}
            readMappings={readCurrentMappings}
            missingAiKeys={missingAiKeys}
          />
          <InputTab part="mapping" {...inputProps} />
          <SettingSection tile="test">
            <div className={settingsStyles.settingBody}>
              <span className={settingsStyles.settingHint}>
                {t['com.streamlain.automation.sidebar.test-with-rows-hint']()}
              </span>
              <ExecutionPanel
                layout="test"
                {...executionProps}
                onTestCreated={() => {
                  if (config.workflowId) {
                    openWorkflowBuilder(
                      editorService?.editor,
                      config.workflowId,
                      workflowName
                    );
                  }
                }}
              />
            </div>
          </SettingSection>
        </SettingUnit>
        <SettingUnit name="failure" tiles={FAILURE_UNIT_TILES}>
          <ProcessTab
            part="failure"
            config={config}
            onUpdate={handleUpdate}
            readMappings={readCurrentMappings}
          />
        </SettingUnit>
      </>
    ),
    output: (
      <SettingUnit name="output" tiles={tilesOfPane('output')}>
        <OutputTab
          config={config}
          onUpdate={handleUpdate}
          targetColumns={targetColumns}
          tableColumnsMap={tableColumnsMap}
          sourceColumns={sourceColumns}
          // sl-3nk: Only owner can configure AI relation matching
          isOwner={config.createdBy === currentUserId}
        />
      </SettingUnit>
    ),
    general: (
      <>
        <SettingUnit name="trigger" tiles={TRIGGER_UNIT_TILES}>
          <SettingSection tile="trigger">
            <div className={settingsStyles.settingBody}>
              {triggerSettings}
              {/* sl-ufq6.3.2: Budget Group Assignment */}
              <AutomationGroupSelector
                workspaceId={workspace.id}
                automationBlockId={activeBlockId}
              />
            </div>
          </SettingSection>
        </SettingUnit>
        <SettingUnit name="hub">
          <HubShareSection
            state={hubShareState}
            onShare={() => setPublishWizardOpen(true)}
          />
        </SettingUnit>
        <SettingUnit name="history" tiles={HISTORY_UNIT_TILES}>
          <SettingSection tile="history">
            <HistoryPanel blockId={activeBlockId} />
          </SettingSection>
        </SettingUnit>
      </>
    ),
  };

  const statusLabel =
    readiness.status === 'setup'
      ? t.t('com.streamlain.automation.sidebar.status.steps-ready', {
          ready: countReadyParts(readiness),
          total: READINESS_PART_COUNT,
        })
      : t.t(`com.streamlain.automation.sidebar.status.${readiness.status}`);

  const flowIcons: Record<SettingsPane, ReactNode> = {
    general: TRIGGER_ICONS[trigger],
    input: <TableIcon />,
    workflow: <PlayIcon />,
    output: <TableIcon />,
  };

  const sourceDocId = config.sourceDocId;
  const sourceTableId = config.sourceTableId;
  const runActions = (
    <ExecutionPanel
      layout="footer"
      {...executionProps}
      chosenRows={
        manualRunOverTable
          ? {
              rowIds: chosenSourceRows.rowIds,
              onOpenTable:
                isCrossDoc && sourceDocId && sourceTableId
                  ? () =>
                      workbenchService.workbench.openDoc({
                        docId: sourceDocId,
                        blockIds: [sourceTableId],
                      })
                  : undefined,
            }
          : null
      }
      runLocked={!readiness.canRun}
      runLockedHint={
        firstRunBlocker ? t.t(firstRunBlocker.labelKey) : undefined
      }
      onRunLockedClick={
        firstRunBlocker ? () => resolveIssue(firstRunBlocker) : undefined
      }
    />
  );

  const safetyNotice =
    safetyDiagnostic && safetyDiagnostic.topology.state !== 'SAFE' ? (
      <div
        className={startStyles.safetyAlert}
        role={
          safetyDiagnostic.topology.state === 'EXTERNAL_UNVERIFIABLE'
            ? 'status'
            : 'alert'
        }
        aria-live="polite"
      >
        <strong>
          {safetyPresentation ? t.t(safetyPresentation.titleKey) : null}
        </strong>
        {safetyDiagnostic.incidents[0]?.path.length ? (
          <ol
            aria-label={t.t(
              'com.streamlain.automation.overview.setup.cycle-path'
            )}
          >
            {safetyDiagnostic.incidents[0].path.map((segment, index) => {
              const step = presentLoopPathSegment(segment);
              if (step.kind === 'hidden') {
                return (
                  <li key={`hidden:${index}`}>
                    {t['com.streamlain.automation-safety.restricted-step']()}
                  </li>
                );
              }
              const label =
                step.label ??
                t['com.streamlain.automation-safety.untitled-table']();
              const target = step.target;
              return (
                <li
                  key={`${step.resourceType}:${target?.blockId ?? label}:${index}`}
                >
                  {target ? (
                    <button
                      type="button"
                      className={startStyles.safetyPathLink}
                      onClick={() =>
                        workbenchService.workbench.openDoc({
                          docId: target.docId,
                          blockIds: [target.blockId],
                        })
                      }
                    >
                      {label}
                    </button>
                  ) : (
                    label
                  )}
                </li>
              );
            })}
          </ol>
        ) : null}
      </div>
    ) : null;

  const runsAsOther =
    configRead?.schedule?.enabled &&
    configRead.runsAs &&
    configRead.runsAs.userId !== currentUserId
      ? configRead
      : null;
  const runsAsNotice = runsAsOther ? (
    <div
      className={takeoverStyles.runsAs}
      role="status"
      data-testid="automation-runs-as"
    >
      <span>
        {t['com.streamlain.automation.takeover.runs-as']({
          name:
            runsAsOther.runsAs?.name ??
            t['com.streamlain.automation.takeover.someone'](),
        })}
      </span>
      <Button
        data-testid="automation-take-over"
        onClick={() => setTakeoverOpen(true)}
      >
        {t['com.streamlain.automation.takeover.confirm']()}
      </Button>
    </div>
  ) : null;

  return (
    <div className={styles.panel}>
      {isScheduleSaving && (
        <div
          className={styles.scheduleSavingOverlay}
          role="status"
          aria-live="polite"
          aria-label={t['com.streamlain.streamlain.common.saving']()}
        >
          <Loading size={24} />
          <strong>{t['com.streamlain.streamlain.common.saving']()}</strong>
          <span>
            {t['com.streamlain.streamlain.process.schedule-active-info']()}
          </span>
        </div>
      )}
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => automationService.setActiveBlock(null)}
          aria-label={t['com.streamlain.streamlain.common.back-to-overview']()}
          title={t['com.streamlain.streamlain.common.back-to-overview']()}
        >
          <ArrowLeftSmallIcon width={20} height={20} />
        </button>
        <div className={startStyles.headerIdentity}>{detailTitleEditor}</div>
        <button
          type="button"
          className={startStyles.headerSettings}
          aria-pressed={settingsTile !== null}
          aria-label={t['com.streamlain.automation.sidebar.open-settings']()}
          title={t['com.streamlain.automation.sidebar.open-settings']()}
          onClick={() =>
            setSettingsTile(current =>
              current !== null ? null : (readiness.issues[0]?.tile ?? 'source')
            )
          }
          data-testid="automation-open-settings"
        >
          <SettingsIcon />
        </button>
      </div>

      <AutomationStartPage
        readiness={readiness}
        summaries={tileValues}
        icons={flowIcons}
        hubSummary={t.t(hubShareSummaryKey(hubShareState))}
        blockId={activeBlockId}
        workspaceId={workspace.id}
        activePane={settingsTile === null ? null : paneOfTile(settingsTile)}
        onTogglePane={togglePane}
        statusLabel={statusLabel}
        onOpenIssue={resolveIssue}
        activation={activationSwitch}
        historyOpen={settingsTile === 'history'}
        onToggleHistory={() =>
          setSettingsTile(current => (current === 'history' ? null : 'history'))
        }
        notice={
          runsAsNotice || safetyNotice ? (
            <>
              {runsAsNotice}
              {safetyNotice}
            </>
          ) : null
        }
        actions={runActions}
      />

      <AutomationSettingsPanel
        host={settingsHost}
        open={settingsTile !== null}
        title={detailAutomationTitle}
        focusTile={settingsTile ?? 'source'}
        attentionTile={attentionTile === settingsTile ? attentionTile : null}
        onAttentionEnd={clearAttention}
        readiness={readiness}
        icons={flowIcons}
        groups={settingsGroups}
        onClose={() => setSettingsTile(null)}
        onReveal={tile => {
          // Adding status tracking means using it: it starts switched on.
          if (tile === 'tracking' && !config.statusTracking?.enabled)
            handleUpdate({
              statusTracking: {
                ...(config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG),
                enabled: true,
              },
            });
        }}
      />

      {takeoverOpen && configRead && automationInfo?.docId ? (
        <AutomationTakeoverDialog
          workspaceId={workspace.id}
          docId={automationInfo.docId}
          blockId={activeBlockId}
          read={configRead}
          onClose={() => setTakeoverOpen(false)}
          onTakenOver={read => {
            setConfigRead(read);
            setTakeoverOpen(false);
            window.__automationRuntimeBridge?.refreshSchedule(
              activeBlockId,
              automationInfo.docId
            );
            notify.success({
              title: t['com.streamlain.automation.takeover.done'](),
            });
          }}
        />
      ) : null}

      {publishWizardOpen && (
        <PublishHubEntry
          open={publishWizardOpen}
          onClose={() => setPublishWizardOpen(false)}
          input={buildAutomationTemplatePublishSource({
            workspaceId: workspace.id,
            automationBlockId: activeBlockId,
            workflowId: config.workflowId ?? undefined,
            title: config.title ?? '',
          })}
        />
      )}
    </div>
  );
};
